// =============================================================================
// APP — Root Application Component
// =============================================================================
// Main application layout composing:
// SkipToContent → Navbar → Hero → Menu → About → Gallery → Contact & Map → Delivery → Footer
// + CookieConsent & LegalModal (Privacy, Terms, Accessibility, Cookie Settings)
// Fully localized across all text, categories, items, badges, and opening hours.
// =============================================================================

import { useTranslation } from 'react-i18next';
import { useState, useCallback } from 'react';
import {
  SkipToContent,
  Navbar,
  Hero,
  MenuSection,
  Footer,
  CookieConsent,
  LegalModal,
  CTAButton,
  LanguageSwitcher,
  DeliveryButtons,
  generateRestaurantSchema,
  type NavLink,
  type Language,
  type LegalModalType,
} from '@webzite/core';
import { OpenStreetMapEmbed, GoogleMapsEmbed } from '@webzite/integrations';
import { validateRestaurant, validateMenu } from '@webzite/content-schema';

// Import content data
import restaurantJson from '../content/restaurant.json';
import menuJson from '../content/menu.json';
import siteConfig from '../site.config.js';

// Validate content at runtime
const restaurant = validateRestaurant(restaurantJson);
const menu = validateMenu(menuJson);

export function App() {
  const { t, i18n } = useTranslation();
  const [activeModal, setActiveModal] = useState<LegalModalType>(null);

  // --- Navigation links ---
  const navLinks: NavLink[] = [
    { id: 'menu', label: t('nav.menu'), href: '#menu' },
    { id: 'about', label: t('nav.about'), href: '#about' },
  ];

  if (restaurant.features.gallery && restaurant.gallery.length > 0) {
    navLinks.push({ id: 'gallery', label: t('nav.gallery'), href: '#gallery' });
  }

  navLinks.push({ id: 'contact', label: t('nav.contact'), href: '#contact' });

  // --- Languages ---
  const languages: Language[] = siteConfig.locales.map((code) => ({
    code,
    label: code.toUpperCase(),
  }));

  const handleLanguageChange = useCallback(
    (locale: string) => {
      void i18n.changeLanguage(locale);
    },
    [i18n],
  );

  const reservationUrl = siteConfig.reservations.bookingUrl ?? '#contact';
  const openTableUrl = 'https://www.opentable.fr/r/la-table-du-prince-paris?ref=18936';
  const localizedTagline = t('hero.tagline', { defaultValue: restaurant.tagline });
  const localizedStory = restaurant.about
    ? t('about.story', { defaultValue: restaurant.about.story })
    : undefined;

  return (
    <>
      {/* Skip-to-content link for keyboard accessibility */}
      <SkipToContent />

      {/* Navigation bar (distinct glassmorphic top, solid scroll, responsive hamburger < 1024px) */}
      <Navbar
        siteName={restaurant.name}
        links={navLinks}
        ctaText={restaurant.features.reservations ? t('nav.reserve') : undefined}
        ctaHref={restaurant.features.reservations ? reservationUrl : undefined}
        languageSwitcher={
          <LanguageSwitcher
            currentLocale={i18n.language}
            languages={languages}
            onLanguageChange={handleLanguageChange}
          />
        }
      />

      {/* Main content */}
      <main id="main-content">
        {/* Hero section */}
        <Hero
          headline={restaurant.name}
          tagline={localizedTagline}
          backgroundImage={restaurant.gallery[0]?.src ?? '/images/hero.png'}
          backgroundAlt={restaurant.gallery[0]?.alt ?? 'Restaurant interior'}
          primaryCTA={
            restaurant.features.reservations ? (
              <CTAButton as="link" href={reservationUrl} variant="primary">
                {t('hero.cta.reserve')}
              </CTAButton>
            ) : undefined
          }
          secondaryCTA={
            <CTAButton as="link" href="#menu" variant="secondary">
              {t('hero.cta.menu')}
            </CTAButton>
          }
        />

        {/* Menu section (clean typographic badges, fully localized categories & dishes) */}
        <MenuSection
          menu={menu}
          currency={siteConfig.currency}
          locale={i18n.language}
          heading={t('menu.heading')}
          allergenNote={t('menu.allergens.note')}
        />

        {/* About section */}
        {restaurant.about && (
          <section className="section" id="about" aria-labelledby="about-heading">
            <div className="container container-narrow text-center">
              <h2 id="about-heading">{t('about.heading')}</h2>
              <p
                style={{
                  marginBlockStart: 'var(--space-6)',
                  color: 'var(--color-on-surface-muted)',
                  fontSize: 'var(--text-lg)',
                  lineHeight: '1.7',
                }}
              >
                {localizedStory}
              </p>
              {restaurant.about.chefName && (
                <p
                  style={{
                    marginBlockStart: 'var(--space-4)',
                    fontWeight: 'var(--font-weight-semibold)',
                    color: 'var(--color-primary)',
                  }}
                >
                  — {restaurant.about.chefName}
                </p>
              )}
            </div>
          </section>
        )}

        {/* Gallery Section */}
        {restaurant.features.gallery && restaurant.gallery.length > 0 && (
          <section
            className="section gallery-section"
            id="gallery"
            aria-labelledby="gallery-heading"
          >
            <div className="container">
              <h2
                id="gallery-heading"
                className="text-center"
                style={{ marginBlockEnd: 'var(--space-8)' }}
              >
                {t('gallery.heading')}
              </h2>
              <div className="gallery-grid-layout">
                {restaurant.gallery.map((img, idx) => (
                  <div
                    key={idx}
                    className="gallery-card-item"
                  >
                    <img
                      src={img.src}
                      alt={img.alt}
                      loading="lazy"
                      className="gallery-card-image"
                    />
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Contact, Reservations & Map section */}
        <section
          className="section contact-section"
          id="contact"
          aria-labelledby="contact-heading"
        >
          <div className="container">
            <h2
              id="contact-heading"
              className="text-center"
              style={{ marginBlockEnd: 'var(--space-10)' }}
            >
              {t('contact.heading')}
            </h2>

            <div className="contact-grid-layout">
              {/* Contact Information & Localized Hours */}
              <div>
                <h3>{t('contact.address')}</h3>
                <address className="contact-address-box">
                  <p className="contact-street">
                    {restaurant.address.streetAddress}
                  </p>
                  <p className="contact-locality">
                    {restaurant.address.postalCode} {restaurant.address.addressLocality}
                  </p>
                  <p className="contact-detail-row">
                    <strong>{t('contact.phone')}:</strong>{' '}
                    <a href={`tel:${restaurant.phone.replace(/\s+/g, '')}`}>{restaurant.phone}</a>
                  </p>
                  <p>
                    <strong>{t('contact.email')}:</strong>{' '}
                    <a href={`mailto:${restaurant.email}`}>{restaurant.email}</a>
                  </p>
                </address>

                {/* Online Reservations CTAs */}
                {restaurant.features.reservations && (
                  <div className="contact-reservations-wrapper">
                    <h4>{t('nav.reserve')}</h4>
                    <div className="contact-reservations-buttons">
                      <CTAButton as="link" href={reservationUrl} variant="primary">
                        TheFork
                      </CTAButton>
                      <CTAButton as="link" href={openTableUrl} variant="secondary">
                        OpenTable
                      </CTAButton>
                    </div>
                  </div>
                )}

                <h3 style={{ marginBlockStart: 'var(--space-8)' }}>{t('contact.hours')}</h3>
                <ul
                  role="list"
                  className="contact-hours-list"
                >
                  {restaurant.hours.map((spec, i) => {
                    const localizedDays = spec.dayOfWeek
                      .map((d) => t(`days.${d.toLowerCase()}`, { defaultValue: d }))
                      .join(', ');

                    return (
                      <li
                        key={i}
                        className="contact-hours-item"
                      >
                        <strong>{localizedDays}</strong>: {spec.opens} – {spec.closes}
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Interactive Map Embed with Localized Labels */}
              <div>
                <h3>{t('contact.map', 'Map')}</h3>
                <div className="contact-map-wrapper">
                  {siteConfig.maps.provider === 'google' ? (
                    <GoogleMapsEmbed
                      embedUrl={siteConfig.maps.embedUrl ?? ''}
                      googleMapsLink={siteConfig.maps.googleMapsLink}
                      title={restaurant.name}
                      loadButtonText={t('map.click')}
                      openGoogleMapsText={t('map.open.google')}
                    />
                  ) : (
                    <OpenStreetMapEmbed
                      latitude={restaurant.geo.latitude}
                      longitude={restaurant.geo.longitude}
                      address={`${restaurant.address.streetAddress}, ${restaurant.address.postalCode} ${restaurant.address.addressLocality}`}
                      embedUrl={siteConfig.maps.embedUrl}
                      googleMapsLink={siteConfig.maps.googleMapsLink}
                      appleMapsLink={siteConfig.maps.appleMapsLink}
                      title={restaurant.name}
                      loadButtonText={t('map.click')}
                      openGoogleMapsText={t('map.open.google')}
                      openAppleMapsText={t('map.open.apple')}
                      variant="white"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Delivery buttons */}
        {siteConfig.delivery.enabled && siteConfig.delivery.platforms.length > 0 && (
          <section className="section text-center">
            <div className="container">
              <DeliveryButtons
                platforms={siteConfig.delivery.platforms}
                heading={t('delivery.heading')}
              />
            </div>
          </section>
        )}
      </main>

      {/* Footer (Localized hours, tagline, reservation links, legal links, developer attribution) */}
      <Footer
        restaurant={restaurant}
        tagline={localizedTagline}
        developer={siteConfig.developer}
        reservationLinks={[
          {
            label: 'TheFork',
            url: 'https://www.thefork.fr/restaurant/table-du-prince-r813818#booking=',
          },
          {
            label: 'OpenTable',
            url: 'https://www.opentable.fr/r/la-table-du-prince-paris?ref=18936',
          },
        ]}
        secondaryLinks={[
          { label: t('nav.menu'), href: '#menu' },
          { label: t('nav.about'), href: '#about' },
          { label: t('nav.contact'), href: '#contact' },
        ]}
        onOpenLegal={(type) => setActiveModal(type)}
        onOpenCookieSettings={() => setActiveModal('cookies')}
        labels={{
          hours: t('footer.hours'),
          contact: t('footer.contact'),
          quickLinks: t('footer.quicklinks'),
          privacy: t('footer.privacy'),
          terms: t('footer.terms'),
          accessibility: t('footer.accessibility'),
          cookieSettings: t('footer.cookies'),
          allRightsReserved: t('footer.rights'),
          craftedBy: t('footer.craftedBy'),
        }}
        deliveryButtons={
          siteConfig.delivery.enabled ? (
            <DeliveryButtons platforms={siteConfig.delivery.platforms} />
          ) : undefined
        }
      />

      {/* Legal & Cookie Settings Modals */}
      <LegalModal
        type={activeModal}
        onClose={() => setActiveModal(null)}
        restaurant={restaurant}
        locale={i18n.language}
      />

      {/* Cookie consent banner (GDPR) */}
      {(siteConfig.region === 'eu' || siteConfig.region === 'uk') && (
        <CookieConsent
          heading={t('cookies.heading')}
          description={t('cookies.description')}
          acceptText={t('cookies.accept')}
          rejectText={t('cookies.reject')}
          onOpenSettings={() => setActiveModal('cookies')}
        />
      )}

      {/* JSON-LD structured data for SEO & AI with developer creator attribution */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            generateRestaurantSchema(
              restaurant,
              'https://latableduprince.fr',
              siteConfig.developer,
            ),
          ),
        }}
      />
    </>
  );
}
